import { createFileRoute } from '@tanstack/react-router';
import type { IconName } from 'lucide-react/dynamic';
import { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { Content } from '@/components/ui/content';
import { Icon } from '@/components/ui/icon';
import { Segmented } from '@/components/ui/segmented';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import {
  DeliveryBadge,
  STATUS_LABELS,
  StatusBadge,
} from '@/pages/_app/sales/+components/status';
import { useIsAuthenticated, usePermissions } from '@/services/auth';
import { useDynamicList } from '@/services/dynamic-resource';

export const Route = createFileRoute('/_app/')({
  head: () => meta({ label: 'Dashboard' }),
  component: RouteComponent,
});

const STATUS_CHART_CONFIG: ChartConfig = {
  count: { label: 'Pedidos', color: 'var(--color-primary)' },
};

const REVENUE_CHART_CONFIG: ChartConfig = {
  total: { label: 'Recebido', color: 'var(--color-primary)' },
};

const PERIOD_OPTIONS = [
  { value: '7', label: '7d' },
  { value: '14', label: '14d' },
  { value: '30', label: '30d' },
] as const;

const RECENT_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'overdue', label: 'Atrasados' },
  { value: 'open', label: 'Em aberto' },
] as const;

interface Shortcut {
  label: string;
  description: string;
  url: string;
  icon: IconName;
  permission?: [string, string];
}

const SHORTCUTS: Shortcut[] = [
  {
    label: 'Pedidos',
    description: 'Ver todos os pedidos',
    url: '/sales',
    icon: 'shopping-cart',
  },
  {
    label: 'Clientes',
    description: 'Gerenciar clientes',
    url: '/customers',
    icon: 'user-round',
  },
  {
    label: 'Formas de Pagamento',
    description: 'Configurar pagamentos',
    url: '/payment-methods',
    icon: 'credit-card',
  },
  {
    label: 'Catálogo',
    description: 'Tipos de camisa',
    url: '/shirt-types',
    icon: 'shirt',
  },
  {
    label: 'Usuários',
    description: 'Gerenciar acessos',
    url: '/users',
    icon: 'users',
    permission: ['users', 'browse'],
  },
  {
    label: 'Telas',
    description: 'Configurar telas dinâmicas',
    url: '/screens',
    icon: 'layout-panel-top',
    permission: ['screens', 'browse'],
  },
];

function daysOverdue(sale: any): number {
  if (['delivered', 'canceled'].includes(sale.status)) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const delivery = new Date(sale.deliveryDate);
  delivery.setHours(0, 0, 0, 0);
  return Math.round((today.getTime() - delivery.getTime()) / (1000 * 60 * 60 * 24));
}

function RouteComponent() {
  const { push } = useRouter();
  const { hasPermission } = usePermissions();
  const { user } = useIsAuthenticated();
  const firstName = user?.name?.split(' ')[0];

  const [period, setPeriod] = useState<(typeof PERIOD_OPTIONS)[number]['value']>('14');
  const [recentFilter, setRecentFilter] =
    useState<(typeof RECENT_FILTER_OPTIONS)[number]['value']>('all');

  const { data: salesResponse, isLoading } = useDynamicList('sales', { perPage: 500 });
  const sales = salesResponse?.data ?? [];

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const todayLabel = today.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  // Faturamento é caixa recebido, não valor vendido: cada pedido vira até 2 eventos
  // (entrada e saldo), contados só quando a data de pagamento correspondente já ocorreu.
  const cashEvents = useMemo<{ date: Date; amount: number }[]>(() => {
    return sales
      .filter((s: any) => s.status !== 'canceled')
      .flatMap((s: any) => {
        const events: { date: Date; amount: number }[] = [];
        const depositAmount = s.depositAmount ?? 0;

        if (s.depositPaidAt) {
          events.push({ date: new Date(s.depositPaidAt), amount: depositAmount });
        }
        if (s.balancePaidAt) {
          events.push({
            date: new Date(s.balancePaidAt),
            amount: (s.total ?? 0) - depositAmount,
          });
        }

        return events;
      });
  }, [sales]);

  const stats = useMemo(() => {
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const pending = sales.filter((s: any) => s.status === 'pending').length;

    const overdueSales = sales.filter((s: any) => daysOverdue(s) > 0);
    const overdue = overdueSales.length;

    const revenueThisMonth = cashEvents
      .filter((e) => e.date >= startOfMonth)
      .reduce((sum, e) => sum + e.amount, 0);

    // Quanto ainda falta receber: total menos o que já foi pago (entrada e/ou saldo),
    // somado em cima de todo pedido ativo — inclui tanto quem ainda nem pagou a
    // entrada quanto quem só falta o saldo.
    const outstandingBalance = sales
      .filter((s: any) => s.status !== 'canceled')
      .reduce((sum: number, s: any) => {
        const depositAmount = s.depositAmount ?? 0;
        const paid =
          (s.depositPaidAt ? depositAmount : 0) +
          (s.balancePaidAt ? (s.total ?? 0) - depositAmount : 0);
        return sum + ((s.total ?? 0) - paid);
      }, 0);

    return {
      total: sales.length,
      pending,
      overdue,
      overdueSales,
      revenueThisMonth,
      outstandingBalance,
    };
  }, [sales, cashEvents, today]);

  const topOverdue = useMemo(() => {
    return [...stats.overdueSales]
      .sort((a, b) => daysOverdue(b) - daysOverdue(a))
      .slice(0, 3);
  }, [stats.overdueSales]);

  const statusChartData = useMemo(() => {
    const data = Object.entries(STATUS_LABELS).map(([value, label]) => ({
      status: label,
      value,
      count: sales.filter((s: any) => s.status === value).length,
    }));
    const max = Math.max(...data.map((d) => d.count), 0);
    return data.map((d) => ({ ...d, isMax: d.count === max && max > 0 }));
  }, [sales]);

  const revenueChartData = useMemo(() => {
    const numDays = Number(period);
    const days = Array.from({ length: numDays }).map((_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (numDays - 1 - i));
      date.setHours(0, 0, 0, 0);
      return date;
    });

    return days.map((day) => {
      const total = cashEvents
        .filter((e) => {
          const eventDay = new Date(e.date);
          eventDay.setHours(0, 0, 0, 0);
          return eventDay.getTime() === day.getTime();
        })
        .reduce((sum, e) => sum + e.amount, 0);

      return {
        date: day.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        total,
      };
    });
  }, [cashEvents, period]);

  const recentSales = useMemo(() => {
    const sorted = [...sales].sort(
      (a: any, b: any) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    const filtered = sorted.filter((s: any) => {
      if (recentFilter === 'overdue') return daysOverdue(s) > 0;
      if (recentFilter === 'open') return !['delivered', 'canceled'].includes(s.status);
      return true;
    });

    return filtered.slice(0, 8);
  }, [sales, recentFilter]);

  const visibleShortcuts = SHORTCUTS.filter(
    (s) => !s.permission || hasPermission(s.permission[0] as any, s.permission[1] as any),
  );

  return (
    <Content
      breadcrumbs={{
        title: 'Painel Operacional',
        icon: 'layout-dashboard',
        items: [{ label: 'Dashboard', active: true }],
      }}
    >
      <div className="space-y-5">
        {/* Hero */}
        <div
          className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4 animate-fade-up"
          style={{ animationDelay: '0ms' }}
        >
          <Card className="justify-center">
            <CardContent className="space-y-4">
              <Badge variant="outline" className="capitalize">
                <Icon name="calendar" className="size-3" />
                {todayLabel}
              </Badge>

              <div>
                <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-[-0.02em]">
                  Bem-vindo de volta{firstName ? `, ${firstName}` : ''}.
                </h1>
                <p className="text-muted-foreground mt-1.5 max-w-xl">
                  {isLoading ? (
                    'Carregando os números do dia...'
                  ) : (
                    <>
                      Você tem{' '}
                      <strong className="text-destructive font-bold">
                        {stats.overdue} pedidos atrasados
                      </strong>{' '}
                      e{' '}
                      <strong className="text-warning font-bold">
                        {stats.pending} pendentes
                      </strong>{' '}
                      aguardando confirmação. A produção precisa de atenção hoje.
                    </>
                  )}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="destructive"
                  icon="triangle-alert"
                  onClick={() => push('/sales')}
                >
                  Resolver atrasos
                </Button>
                <Button variant="outline" icon="file-text" onClick={() => push('/sales')}>
                  Relatório mensal
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-sm">Precisa de atenção</CardTitle>
              <button
                type="button"
                onClick={() => push('/sales')}
                className="text-xs font-semibold text-primary hover:underline"
              >
                Ver todos
              </button>
            </CardHeader>
            <CardContent className="space-y-1">
              {topOverdue.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">
                  Nenhum pedido atrasado. 🎉
                </p>
              ) : (
                topOverdue.map((sale: any) => (
                  <div
                    key={sale.id}
                    onClick={() => push(`/sales/${sale.id}/view`)}
                    className="flex items-center gap-3 py-2 cursor-pointer rounded-[10px] hover:bg-muted px-1 -mx-1 transition-colors"
                  >
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-destructive-soft text-destructive text-xs font-bold font-mono">
                      {daysOverdue(sale)}d
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">
                        {sale.customer?.name ?? '—'}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {STATUS_LABELS[sale.status] ?? sale.status}
                      </div>
                    </div>
                    <strong className="text-xs font-mono shrink-0">
                      {(sale.total ?? 0).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </strong>
                  </div>
                ))
              )}

              <div className="border-t border-dashed border-border pt-3 mt-2">
                <p className="text-[11px] text-muted-foreground">
                  Pedidos parados por mais de 15 dias travam a cadeia de mão.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* KPIs */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 animate-fade-up"
          style={{ animationDelay: '60ms' }}
        >
          <StatCard
            label="Total de pedidos"
            value={stats.total}
            icon="shopping-cart"
            loading={isLoading}
          />
          <StatCard
            label="Pendentes"
            value={stats.pending}
            icon="clock"
            loading={isLoading}
            variant={stats.pending > 0 ? 'warning' : undefined}
          />
          <StatCard
            label="Atrasados"
            value={stats.overdue}
            icon="triangle-alert"
            loading={isLoading}
            variant={stats.overdue > 0 ? 'destructive' : undefined}
          />
          <StatCard
            label="Recebido no mês"
            value={stats.revenueThisMonth.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })}
            icon="trending-up"
            loading={isLoading}
          />
          <StatCard
            label="Saldo a receber"
            value={stats.outstandingBalance.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })}
            icon="hand-coins"
            loading={isLoading}
          />
        </div>

        {/* Gráficos */}
        <div
          className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-4 animate-fade-up"
          style={{ animationDelay: '120ms' }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Fluxo de produção</CardTitle>
              <CardDescription>Pedidos distribuídos nas etapas atuais</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={STATUS_CHART_CONFIG} className="h-64 w-full">
                <BarChart data={statusChartData}>
                  <CartesianGrid vertical={false} stroke="var(--color-border)" />
                  <XAxis
                    dataKey="status"
                    tickLine={false}
                    axisLine={false}
                    fontSize={11}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={50}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    width={30}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="count" radius={4}>
                    {statusChartData.map((entry) => (
                      <Cell
                        key={entry.value}
                        fill={
                          entry.value === 'canceled'
                            ? 'transparent'
                            : entry.isMax
                              ? 'var(--color-primary)'
                              : 'var(--color-primary-soft)'
                        }
                        stroke={entry.isMax ? undefined : 'var(--color-primary-line)'}
                        strokeDasharray={entry.value === 'canceled' ? '4 3' : undefined}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <div>
                <CardTitle>Entrada de caixa</CardTitle>
                <CardDescription>Valor efetivamente recebido por dia</CardDescription>
              </div>
              <Segmented
                value={period}
                onChange={setPeriod}
                options={[...PERIOD_OPTIONS]}
              />
            </CardHeader>
            <CardContent>
              <ChartContainer config={REVENUE_CHART_CONFIG} className="h-64 w-full">
                <AreaChart data={revenueChartData}>
                  <CartesianGrid vertical={false} stroke="var(--color-border)" />
                  <XAxis dataKey="date" tickLine={false} axisLine={false} fontSize={11} />
                  <YAxis tickLine={false} axisLine={false} width={40} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="var(--color-primary)"
                    fill="var(--color-primary)"
                    fillOpacity={0.15}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>

        {/* Acesso rápido */}
        <div className="animate-fade-up" style={{ animationDelay: '180ms' }}>
          <h3 className="text-xs font-bold uppercase tracking-[0.08em] text-faint mb-3">
            Acesso rápido
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {visibleShortcuts.map((shortcut) => (
              <Card
                key={shortcut.url}
                variant="flat"
                interactive
                className="border border-border rounded-[14px] p-4 items-center text-center gap-2"
                onClick={() => push(shortcut.url)}
              >
                <Icon name={shortcut.icon} className="size-6 text-primary" />
                <div className="text-sm font-semibold">{shortcut.label}</div>
                <div className="text-xs text-muted-foreground">
                  {shortcut.description}
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Pedidos recentes */}
        <Card className="animate-fade-up" style={{ animationDelay: '240ms' }}>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>Pedidos recentes</CardTitle>
            <Segmented
              value={recentFilter}
              onChange={setRecentFilter}
              options={[...RECENT_FILTER_OPTIONS]}
            />
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent cursor-default">
                  <TableHead>Pedido</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Prazo</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentSales.map((sale: any) => (
                  <TableRow key={sale.id} onClick={() => push(`/sales/${sale.id}/view`)}>
                    <TableCell className="font-mono text-xs">
                      {sale.saleNumber ?? sale.id.slice(0, 8)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {sale.customer?.name ?? '—'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={sale.status} />
                    </TableCell>
                    <TableCell>
                      <DeliveryBadge
                        deliveryDate={sale.deliveryDate}
                        status={sale.status}
                      />
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {(sale.total ?? 0).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </Content>
  );
}

function StatCard({
  label,
  value,
  icon,
  loading,
  variant,
}: {
  label: string;
  value: string | number;
  icon: IconName;
  loading?: boolean;
  variant?: 'warning' | 'destructive';
}) {
  return (
    <Card
      variant="flat"
      className={
        variant === 'destructive'
          ? 'border border-destructive bg-destructive-soft rounded-[14px]'
          : 'border border-border rounded-[14px]'
      }
    >
      <CardContent className="gap-1.5 px-4">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-faint">
            {label}
          </div>
          <Icon
            name={icon}
            className={
              variant === 'destructive'
                ? 'size-4 shrink-0 text-destructive'
                : 'size-4 shrink-0 text-muted-foreground'
            }
          />
        </div>
        <div
          className={
            variant === 'destructive'
              ? 'font-display text-[26px] font-extrabold tracking-[-0.02em] tabular-nums text-destructive truncate'
              : 'font-display text-[26px] font-extrabold tracking-[-0.02em] tabular-nums truncate'
          }
        >
          {loading ? '—' : value}
        </div>
      </CardContent>
    </Card>
  );
}
